import {
  GridRole,
  MatchTargetKey,
  SourceKey,
  SourceTag,
} from './source-key.model'
import { ColumnStorage, ColumnType } from './field-catalog.model'

/**
 * Column-source groups used to organise columns in the Column Manager. `ELM` carries no columns in
 * the current Positions catalogue but is part of the column-source vocabulary, so it stays in the
 * union rather than being reintroduced later as a breaking change.
 */
export type ColSourceGroup =
  | 'Helios'
  | 'Default'
  | 'Position'
  | 'Cash Balance'
  | 'Security Master'
  | 'ELM'
  | 'External'
  | 'Aggregate Info'

/** The leading Helios column-source group label. Its columns are UI-owned (computed), not catalog
 * entries, so the adapter and Column Manager force this group to the front of the section order. */
export const HELIOS_GROUP: ColSourceGroup = 'Helios'

export type CatalogCellType =
  | 'text'
  | 'number'
  | 'money'
  | 'date'
  | 'datetime'
  | 'boolean'
  | 'set'

/** One catalog column: a data-driven description that the ColDef factory turns into an ag-Grid col. */
export interface ColumnCatalogEntry {
  field: string
  headerName: string
  colSource: ColSourceGroup
  /**
   * The column's source group, repeated verbatim as the value the HEADER shows alongside the label
   * (the Column Manager reads {@link colSource} for grouping instead). Duplicate labels across
   * sources are expected and legitimate, and colSource is the ONLY thing that distinguishes them,
   * so it has to be visible on the header too — not only in the Column Manager.
   */
  colSourceLabel?: string
  /** Header tooltip, `<label> · <colSource>`, so a truncated header still disambiguates. */
  headerTooltip?: string
  cellType: CatalogCellType
  /**
   * The catalog's own semantic type, carried through UNMAPPED alongside the rendering-oriented
   * {@link cellType}. The /query contract has to echo a type back on every filter and sort, in two
   * separate vocabularies, and neither is derivable from `cellType` (which collapses NUM and MONEY,
   * and has no TIME). Absent on UI-owned columns that no catalog describes (`calc:`, computed).
   */
  valueType?: ColumnType
  /**
   * Physical storage from the catalog. `AGG_INFO` is the one the request builder must know about:
   * those fields go under `context.fieldStorage.aggInfo` and are the only ones offered a per-column
   * type override. Absent on UI-owned columns.
   */
  storage?: ColumnStorage
  /** Hidden by default (e.g. Aggregate Info columns). */
  hide?: boolean
  width?: number
  /** The column's closed value domain, when it has one — see {@link ColumnDescriptor.allowedValues}. */
  allowedValues?: string[]
}

/** Everything the workbench needs to know about a source: metadata + column catalog. */
export interface SourceDefinition {
  key: SourceKey
  label: string
  tag: SourceTag
  tagColor: string
  color: string
  model: string
  sub: string
  allowedRoles: GridRole[]
  /**
   * Roles where the source is listed in the picker but cannot be chosen. Used for pairings the
   * workbench is designed for but not yet cleared to show: the option stays visible so the
   * eventual capability is discoverable, and greyed out so nobody lands on unfinished data.
   *
   * Must be a subset of {@link allowedRoles} — a role that is not allowed is not rendered at all,
   * so listing it here would be silently meaningless.
   */
  disabledRoles?: GridRole[]
  matchTargets: MatchTargetKey[]
  /** Prometheus expands its AggregateInformation JSON into discovered columns at runtime. */
  hasAggregateInfo?: boolean
  /** Whether the status quick-filter chips (Unmatched/Ignored/Matched) are shown for this source. */
  hasStatusFilter?: boolean
  /** Whether rows of this source can be ignored. Prometheus only. */
  hasIgnore?: boolean
  /** Whether this source exposes the Comments column + View/Add actions. Not Enfusion — raw feed. */
  hasComments?: boolean
  /** Whether this source carries a Custody/Trading sub-source shown in the Source column. */
  hasSubSource?: boolean
  /**
   * Whether the leading Source column is shown (capability flag replacing the old `key==='api'`
   * check in the computed-column builder). True for Enfusion and any sub-sourced feed.
   */
  hasSourceColumn?: boolean
  /**
   * Whether a leading Account ID computed column is shown (capability flag replacing the old
   * `key==='api'` check). Enfusion only — the others carry Account ID in their catalog.
   */
  hasLeadingAccountId?: boolean
  /**
   * Whether the four `calc:` unit-difference columns (External group) are appended as UI-owned
   * columns. Clearwater only — they are computed from the match graph and returned by no backend.
   */
  hasUnitDifferenceColumns?: boolean
  /** Whether the Show Closed Positions panel setting applies. Clearwater only. */
  hasClosedPositionsSetting?: boolean
  /**
   * Whether the Differences Only quick filter applies. Clearwater only.
   *
   * Still a flag rather than an inlined `key === 'cw'` check even though only one quick filter is
   * left: Breaks Only was the second, and it was removed because there was never break data behind
   * it (see `external-filters.ts`), not because the capability stopped being source-scoped.
   */
  hasQuickFilters?: boolean
  /**
   * Whether this source is served by POST /flex-positions/query — i.e. whether its grid filters and
   * sorts SERVER-SIDE.
   *
   * True for Enfusion and Prometheus, false for Clearwater, which has no read endpoint. Two things
   * read it: the Quick Search affordance (a client-side quick filter over a rolling window of loaded
   * blocks would silently search a subset, so it is disabled with a visible reason rather than
   * offered) and the status-bar totals labelling.
   */
  hasServerSideQuery?: boolean
}
